import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Button, Alert, ButtonGroup, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { getAllBillings, updateMarket } from '../../../../slices/auth/settings/thunk';
import PrismCode from "../../../../Components/Common/Prism";
import { setEditMarketModalData, toggleEditMarketModal } from '../../../../slices/modals/reducer';
import CustomSelect from '../../../../Components/Common/CustomSelect';
import country from '../../../../common/country';
import Preloader from '../../../../Components/Common/Preloader';
import SimpleBar from 'simplebar-react';
import CustomSelectWithSearch from '../../../../Components/Common/CustomSelectWithSearch';

/**
 * React component for edit market information in a modal.
 * @component
 */
function EditMarketModal() {
  const dispatch = useDispatch();

  const selectState = createSelector(state => state, state => ({
    editModal: state.Modal.editMarketModal,
    editModalData: state.Modal.editMarketModalData,
    billing: state.Settings.billing,
    client: state.MenuData.client,
    clientId: state.MenuData.clientPath,
    brandId: state.MenuData.brandPath,
    marketId: state.Settings.selectedMarketId
  }))
  const { editModal, editModalData, billing, client, clientId, brandId, marketId } = useSelector(selectState);
  const [formData, setFormData] = useState({});
  const [invoicesData, setInvoicesData] = useState([]);
  const [parentBilling, setParentBilling] = useState(null);
  const [useInvoice, setUseInvoice] = useState(false);

  // form data
  useEffect(() => {
    if (editModalData !== null) {

      setFormData((prevValue) => {
        return {
          ...prevValue,
          name: editModalData.name,
          webSite: editModalData.webSite
        }
      });
    }
  }, [editModalData])

  // billing
  useEffect(() => {
    if (billing) {
      try {
        setInvoicesData(billing);
        const foundClient = client.clients.find(item => item._id === clientId);
        const foundBrand = foundClient.brands.find(item => item._id === brandId);
        const foundParentBilling = billing.find(item => item._id === foundBrand.billingId);
        if (foundParentBilling) {
          setParentBilling(foundParentBilling)
        }
      } catch (error) {

      }
    } else {
      setInvoicesData([]);
    }
  }, [billing]);

  useEffect(() => {
    if (editModalData && invoicesData.length > 0) {
      const foundBilling = invoicesData.find(item => item._id === formData.billingId);
      if (foundBilling) {
        setFormData(foundBilling);
      }
    }
  }, [editModalData, invoicesData]);

  function fillModal(billing) {
    setFormData((prevValue) => {
      return {
        billingId: billing._id,
        billingName: billing.billingName,
        taxNumber: billing.taxNumber,
        billingAddress: billing.billingAddress,
        billingCountry: billing.billingCountry,
        phoneNumber: billing.phoneNumber,
        name: prevValue.name,
        webSite: prevValue.webSite
      }
    });
  }

  /**
   * Toggles the use of company information for invoicing in the form and updates Formik validation accordingly.
   */
  function toggleCompanyForInvoicing() {
    if (!useInvoice && parentBilling) {
      setUseInvoice(true);
      fillModal(parentBilling)
    } else {
      setUseInvoice(false);
      setFormData({
        billingId: null,
        billingName: '',
        taxNumber: '',
        billingAddress: '',
        billingCountry: '',
        phoneNumber: '',
        name: '',
        webSite: ''
      });
    }
  }
  /**
   * Handles a change in the form field value and updates form data state accordingly.
   * @param {event} e - event
   */
  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prevValue) => {
      return {
        ...prevValue,
        [name]: value
      }
    });
  }

  function changeSelect(item) {
    setFormData((prevValue) => {
      return {
        ...prevValue,
        billingCountry: item,
      }
    })
  }

  function closeModal() {
    dispatch(toggleEditMarketModal());
    setTimeout(() => {
      setInvoicesData([]);
      setUseInvoice(false);
      dispatch(setEditMarketModalData(null));
      setFormData({
        billingId: null,
        billingName: '',
        taxNumber: '',
        billingAddress: '',
        billingCountry: '',
        phoneNumber: '',
        name: '',
        webSite: ''
      });
    }, 200);
  }

  /**
   * Handles the edit operation for a market.
   * @param {Event} e - The event object.
   */
  async function editMarket(e) {
    e.preventDefault();
    const marketUpdated = await dispatch(updateMarket({
      ...formData,
      id: marketId,
      brandId: brandId,
      clientId: clientId,
    }));
    if (marketUpdated) {
      closeModal();
    }
  }
  return (
    <Modal isOpen={editModal} toggle={closeModal} centered size='lg' >
      <ModalHeader className='bg-info p-3' toggle={closeModal}>{(editModalData !== null ? editModalData.name : "")}</ModalHeader>
      <form className="tablelist-form" onSubmit={editMarket} onChange={handleChange}>
        <ModalBody>
          {Array.isArray(invoicesData) && invoicesData.length > 0 ?
            <React.Fragment>
              <Row className='mb-3'>
                <Col xxl="12" xl="12" lg="12" md="12" sm="12" className='d-flex justify-content-between align-items-center'>
                  <h5 className='my-4'>Invoicing Details</h5>
                  <ButtonGroup>
                    <UncontrolledDropdown direction='end'>
                      <DropdownToggle tag="span" className='btn btn-icon bg-light text-black border border-0'>
                        <i className="ri-list-unordered fs-20"></i>
                      </DropdownToggle>
                      <DropdownMenu className='dropdown-menu-md'>
                        <div className='dropdown-header noti-title border-bottom mb-2'>
                          <h6 className='fs-12'>List of invoice accounts</h6>
                        </div>
                        <SimpleBar style={{ width: "100%", maxHeight: "220px", overflowX: "hidden" }}>
                          {(invoicesData || []).map((item, index) => {
                            return (
                              <DropdownItem
                                key={index}
                                className='py-2 fs-12 border-bottom'
                                onClick={() => { fillModal(item) }}>
                                <p className='mb-2 fw-bold'>{(item.billingName || "")}</p>
                                <p className='text-wrap my-0'>
                                  <b>Tax Number:</b> {(item.taxNumber || "")}
                                </p>
                                <p className='text-wrap my-0'>
                                  <b>Billing Address:</b> {(item.billingAddress || "")}
                                </p>
                                <p className='text-wrap my-0'>
                                  <b>Billing Country:</b> {(item.billingCountry || "")}
                                </p>
                              </DropdownItem>
                            )
                          })}
                        </SimpleBar>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </ButtonGroup>
                </Col>

                <Col>
                  <label htmlFor='billingName'>Billing Name</label>
                  <input type='text' name='billingName' id='billingName'
                    className='form-control' placeholder='Please enter clients billing name'
                    value={(formData.billingName || "")} required disabled={useInvoice} />
                </Col>
                <Col>
                  <label htmlFor='taxNumber'>Tax Number</label>
                  <input type='text' name='taxNumber' id="taxNumber"
                    className='form-control' placeholder='Please enter clients tax number'
                    value={(formData.taxNumber || "")} required disabled={useInvoice} />
                </Col>
              </Row>

              <Row>
                <Col>
                  <label htmlFor='billingAddress'>Billing Adress</label>
                  <textarea className='form-control' name='billingAddress'
                    rows="5" placeholder='Please enter clients billing address'
                    value={(formData.billingAddress || "")} required disabled={useInvoice}></textarea>
                </Col>

                <Col>
                  <Row>
                    <Col lg={12} sm={12} md={12}>
                      <label htmlFor='billingCountry'>Billing Country</label>
                      <CustomSelectWithSearch
                        className={"form-select"}
                        placeholder='Select Billing Country'
                        options={country}
                        validation={(item) => setFormData((prevValue) => { return { ...prevValue, billingCountry: item } })}
                        onChange={(item) => changeSelect(item.name)}
                        value={(formData.billingCountry || "")}
                        disabled={useInvoice}
                      />
                    </Col>
                    <Col className="mt-2" lg={12} sm={12} md={12}>
                      <label htmlFor="phoneNumber">Phone Number</label>
                      <input type="text" name="phoneNumber" id="phoneNumber"
                        className="form-control" placeholder="Please enter clients phone number"
                        value={(formData.phoneNumber || "")} required disabled={useInvoice} />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </React.Fragment>
            : <Preloader isComponentLoader={true} />}
        </ModalBody>
        <ModalFooter className='d-flex justify-content-between'>
          <div className='d-flex justify-self-start'>
            {parentBilling &&
              <React.Fragment>
                <Input className="form-check-input" type="checkbox" onClick={toggleCompanyForInvoicing} value="" id="invoice-check" />
                <Label className="mx-1 fw-normal form-check-label" htmlFor="invoice-check">Use <b>{parentBilling.billingName}</b> for invoicing</Label>
              </React.Fragment>
            }
          </div>
          <div className="hstack gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
            {Array.isArray(invoicesData) && invoicesData.length > 0 &&
              <button type="submit" className="btn btn-success" id="add-btn">Save Market</button>
            }
          </div>
        </ModalFooter>
      </form>
    </Modal >

  )
}

export default EditMarketModal
